import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from "../../../context/BusinessContext";
import { formatPhone, isPhoneComplete, resolvePhoneCountry } from "../../../util/phone";
import { useTimeFormat, formatClock } from "../../../util/timeFormat";
import { useAlert } from "../../../context/alert/AlertContext";
import { useConfirm } from "../../../context/confirm/ConfirmContext";
import { useNavigation } from "../../../context/NavigationContext";
import { useQueryClient } from "@tanstack/react-query";
import { useClients } from "../../../hooks/queries";
import LoadingComponent from "../../commons/LoadingComponent";
import Drawer from "../../commons/drawer/Drawer";
import ClientCardSection from "./ClientCardSection";
import Pagination from "../../commons/pagination/Pagination";
import { Client } from "../../../model/client/Client";
import { ClientInput, ClientHealthPlanInput } from "../../../service/client/ClientInput";
import { HealthPlan } from "../../../model/business/HealthPlan";
import { getBusinessHealthPlans } from "../../../service/business/health-plan-service";
import {
    deleteClient,
    findInactiveClientByContact,
    getAllClientsByBusinessId,
    saveClient,
    getClientNotes,
    saveClientNote,
    deleteClientNote,
    setClientFavorite,
} from "../../../service/client/client-service";
import { ClientNote } from "../../../model/client/ClientNote";
import { findBusinessSettingByBusinessId } from "../../../service/business/business-service";
import ClinicalRecordsTab from "./ClinicalRecordsTab";
import { getAppointmentsByClient } from "../../../service/appointment/appointment-service";
import { Appointment } from "../../../model/appointment/Appointment";
import { getMyPermissionGrant, getMyMaxDiscountRate, PermissionGrant } from "../../../service/permission/permission-service";
import { TeamMember } from "../../../model/teammember/TeamMember";
import { getTeamMembersByBusinessId } from "../../../service/teammember/team-member-service";
import { MENU } from "../../util/Utils";
import { mediaUrl } from "../../../service/media/media-service";
import "./ClientsComponent.css";

type ClientTab = "info" | "appointments" | "notes" | "discount" | "payment" | "convenios" | "prontuario";

type Draft = ClientInput;

const emptyDraft = (businessId: string): Draft => ({
    businessId,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    notes: "",
});

/** Backend/infra noise that must never reach the user as a toast (QA-STANDARDS §5). */
const TECHNICAL_ERROR = /sql|grammar|exception|constraint|violat|stack|r2dbc|postgres|executemany/i;
const friendlyError = (e: unknown, fallback: string) => {
    const msg = e instanceof Error ? e.message : "";
    return msg && !TECHNICAL_ERROR.test(msg) ? msg : fallback;
};

const initialsOf = (c: Pick<Client, "firstName" | "lastName">) =>
    ((c.firstName?.[0] ?? "?").toUpperCase() +
        (c.lastName?.[0] ?? "").toUpperCase());

const ClientsComponent: React.FC = () => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const timeFmt = useTimeFormat();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const { pendingClientId, pendingClientTab, setPendingClient, setActiveMenu, setPendingAppointmentId, setPendingChatClient } = useNavigation();

    /** Jump to the Appointments screen with this appointment pre-selected.
     *  Appointments treats the id as a global lookup (ignores period). */
    const openAppointmentDetail = (apptId: string | number) => {
        setPendingAppointmentId(String(apptId));
        setActiveMenu(MENU.APPOINTMENTS);
    };

    const businessIdStr = selectedBusiness?.id ? String(selectedBusiness.id) : "";

    // Clients come from React Query so revisiting the screen is instant (cached)
    // and refreshes in the background instead of showing a full-screen spinner.
    const queryClient = useQueryClient();
    const clientsQuery = useClients(businessIdStr);
    const clients: Client[] = clientsQuery.data ?? [];
    const loading = clientsQuery.isLoading; // true only on the first (uncached) load
    const reload = () => queryClient.invalidateQueries({ queryKey: ['clients', businessIdStr] });
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [saving, setSaving] = useState(false);
    /** Snapshot of the draft when the drawer opened — closing with edits asks first. */
    const draftBaselineRef = useRef<string>("");
    const draftRef = useRef<Draft | null>(null);
    draftRef.current = draft;
    /** Raw text of the discount field — lets the user type "2.5" without the
     *  number round-trip eating the trailing dot. The draft holds the number. */
    const [discountText, setDiscountText] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    /** "Favoritos" tab — show only favorite clients. */
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    /** Pagination — keeps the list manageable for large customer bases. */
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    /** Match returned by debounced lookup on email/phone for a NEW client. */
    const [inactiveMatch, setInactiveMatch] = useState<Client | null>(null);
    /** Set when the user accepts the reactivation banner — saveClient will reuse this id. */
    const [pendingReactivationId, setPendingReactivationId] = useState<string | null>(null);

    /** Active tab inside the client drawer. */
    const [activeTab, setActiveTab] = useState<ClientTab>("info");

    // Prontuário: with health mode on, remind staff that clinical content
    // belongs in the (upcoming) Prontuário module, not in free-text notes.
    const [healthMode, setHealthMode] = useState(false);
    useEffect(() => {
        if (!selectedBusiness?.id) { setHealthMode(false); return; }
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const st = await findBusinessSettingByBusinessId(Number(selectedBusiness.id), token);
                setHealthMode(!!st?.healthMode);
            } catch { setHealthMode(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBusiness?.id]);
    /** Appointments fetched for the currently-opened client (for the Appointments tab). */
    const [clientAppointments, setClientAppointments] = useState<Appointment[]>([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);

    /** CLIENT_HISTORY grant — gates the Appointments (history) + Anotações tabs.
     *  canView shows them; canEdit allows adding/deleting notes. Own-scope is
     *  enforced server-side (a provider only sees/edits their own notes). */
    const [historyGrant, setHistoryGrant] = useState<PermissionGrant | null>(null);
    const canViewHistory = !!historyGrant?.canView;
    const canEditHistory = !!historyGrant?.canEdit;
    // CLIENTS grant — gates the Save/Delete buttons on the client form. Fail-open:
    // show until we KNOW canEdit is false (backend still enforces). View-only hides them.
    const [clientGrant, setClientGrant] = useState<PermissionGrant | null>(null);
    const canEditClient = clientGrant ? clientGrant.canEdit : true;

    /** Client annotations (the Anotações tab) for the open client. */
    const [clientNotes, setClientNotes] = useState<ClientNote[]>([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [newNoteText, setNewNoteText] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    /** Team members (to resolve a note author's id → name). */
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

    /** Business health plans (convênios catalog) — populates the Convênios tab plan picker. */
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([]);
    useEffect(() => {
        if (!businessIdStr) { setHealthPlans([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const plans = await getBusinessHealthPlans(Number(businessIdStr), token);
                if (!cancelled) setHealthPlans(plans.filter((p) => p.active !== false));
            } catch { if (!cancelled) setHealthPlans([]); }
        })();
        return () => { cancelled = true; };
    }, [businessIdStr, getAccessTokenSilently]);

    // ---- Convênios (client health-plan memberships) editing ----
    const convenios = draft?.convenios ?? [];
    const updateConvenios = (next: ClientHealthPlanInput[]) =>
        setDraft((prev) => (prev ? { ...prev, convenios: next } : prev));
    const addConvenio = () =>
        updateConvenios([...convenios, { healthPlanId: "", cardNumber: "" }]);
    const patchConvenio = (idx: number, patch: Partial<ClientHealthPlanInput>) =>
        updateConvenios(convenios.map((cv, i) => (i === idx ? { ...cv, ...patch } : cv)));
    const removeConvenio = (idx: number) =>
        updateConvenios(convenios.filter((_, i) => i !== idx));

    /** The caller's max discount rate (%). Caps the discount they may set on a
     *  client. null = not loaded; 0 = "Nenhum" → can't set any discount. The
     *  backend re-validates on saveClient. */
    const [maxDiscountRate, setMaxDiscountRate] = useState<number | null>(null);

    useEffect(() => {
        if (clientsQuery.error) {
            setAlert("danger", friendlyError(clientsQuery.error, t("clients.alerts.failLoad")));
        }
    }, [clientsQuery.error, setAlert, t]);

    // Debounced lookup of an inactive client matching the typed email/phone.
    // Only runs for NEW clients (no draft.id). Shows a reactivation banner.
    useEffect(() => {
        if (!draft || draft.id || !businessIdStr) {
            setInactiveMatch(null);
            return;
        }
        const email = draft.email?.trim() || null;
        const phone = draft.phone?.trim() || null;
        if (!email && !phone) {
            setInactiveMatch(null);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const token = await getAccessTokenSilently();
                const match = await findInactiveClientByContact(businessIdStr, email, phone, token);
                if (!cancelled) {
                    // Don't override a banner the user already accepted/dismissed
                    // unless the matched id changed.
                    setInactiveMatch((prev) => {
                        if (pendingReactivationId && match?.id === pendingReactivationId) return prev;
                        return match;
                    });
                }
            } catch (_) {
                // Silent — search is a hint, not a hard requirement.
            }
        }, 500);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [draft?.id, draft?.email, draft?.phone, businessIdStr, getAccessTokenSilently, pendingReactivationId, draft]);

    const filteredClients = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return clients.filter((c) => {
            if (favoritesOnly && !c.favorite) return false;
            if (!q) return true;
            const haystack = [
                c.firstName, c.lastName, c.email, c.phone,
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(q);
        });
    }, [clients, searchTerm, favoritesOnly]);

    /** Star toggle — favorite/unfavorite a client (CLIENTS edit on the backend).
     *  favBusy blocks double-clicks (two rapid toggles raced and could land inverted). */
    const [favBusy, setFavBusy] = useState<string | null>(null);
    const toggleFavorite = async (c: Client, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (favBusy === String(c.id)) return;
        setFavBusy(String(c.id));
        const next = !c.favorite;
        try {
            const token = await getAccessTokenSilently();
            const saved = await setClientFavorite(String(c.id), next, token);
            queryClient.setQueryData<Client[]>(['clients', businessIdStr], (prev: Client[] | undefined) =>
                (prev ?? []).map((x: Client) => (String(x.id) === String(c.id) ? { ...x, favorite: saved.favorite } : x)));
            setAlert("success", next ? t("clients.alerts.favorited") : t("clients.alerts.unfavorited"));
        } catch (err) {
            setAlert("danger", friendlyError(err, t("clients.alerts.failFavorite")));
        } finally {
            setFavBusy(null);
        }
    };

    const resetReactivationState = () => {
        setInactiveMatch(null);
        setPendingReactivationId(null);
    };

    /** True when the open drawer has edits the user hasn't saved. */
    const isDraftDirty = () =>
        draftRef.current != null && JSON.stringify(draftRef.current) !== draftBaselineRef.current;

    /** Ask before throwing away unsaved edits (drawer close / switch client). */
    const confirmDiscard = async (): Promise<boolean> => {
        if (!isDraftDirty()) return true;
        return confirmDialog({
            title: t("common.confirmTitle"),
            message: t("clients.unsavedConfirm"),
            danger: true,
            confirmLabel: t("common.discard"),
        });
    };

    const startCreate = async () => {
        if (!(await confirmDiscard())) return;
        setSelectedId(null);
        const d = emptyDraft(businessIdStr);
        draftBaselineRef.current = JSON.stringify(d);
        setDraft(d);
        setDiscountText("");
        resetReactivationState();
        setActiveTab("info");
        setClientAppointments([]);
    };

    const startEdit = async (c: Client): Promise<boolean> => {
        // Re-clicking the already-open client is a no-op (no discard prompt).
        if (draftRef.current?.id && String(draftRef.current.id) === String(c.id)) return true;
        if (!(await confirmDiscard())) return false;
        setSelectedId(c.id);
        applyEdit(c);
        return true;
    };

    const applyEdit = (c: Client) => {
        const d: Draft = {
            id: c.id,
            businessId: String(c.businessId) || businessIdStr,
            firstName: c.firstName ?? "",
            lastName: c.lastName ?? "",
            email: c.email ?? "",
            phone: c.phone ?? "",
            notes: c.notes ?? "",
            discountPercent: c.discountPercent ?? null,
            birthDay: c.birthDay ?? null,
            birthMonth: c.birthMonth ?? null,
            birthYear: c.birthYear ?? null,
            convenios: (c.convenios ?? []).map((cv) => ({
                id: cv.id ?? null,
                healthPlanId: cv.healthPlanId,
                cardNumber: cv.cardNumber,
                validThrough: cv.validThrough ?? null,
                holderType: cv.holderType ?? null,
                holderName: cv.holderName ?? null,
            })),
        };
        draftBaselineRef.current = JSON.stringify(d);
        setDraft(d);
        setDiscountText(c.discountPercent != null ? String(c.discountPercent) : "");
        resetReactivationState();
        setActiveTab("info");
    };

    /** Raw drawer reset — no discard prompt (used after save/delete succeed). */
    const closeDrawer = () => {
        setSelectedId(null);
        setDraft(null);
        draftBaselineRef.current = "";
        resetReactivationState();
        setClientAppointments([]);
    };

    const cancelEdit = async () => {
        if (!(await confirmDiscard())) return;
        closeDrawer();
    };

    // Honour an external navigation request (e.g. "View history" in the
    // Calendar appointment drawer) — open the matching client and land on
    // the requested tab. Clears the pending state after consumption so a
    // back-and-forth doesn't re-open.
    useEffect(() => {
        if (!pendingClientId || clients.length === 0) return;
        const target = clients.find((c) => String(c.id) === String(pendingClientId));
        (async () => {
            if (target && (await startEdit(target))) {
                if (pendingClientTab) setActiveTab(pendingClientTab);
            }
            setPendingClient(null, null);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingClientId, clients]);

    // Fetch this client's full appointment history when the drawer opens.
    // Empty for "new client" drafts (no id yet).
    useEffect(() => {
        const id = draft?.id;
        if (!id) {
            setClientAppointments([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoadingAppointments(true);
                const token = await getAccessTokenSilently();
                const data = await getAppointmentsByClient(String(id), token);
                if (!cancelled) setClientAppointments(data);
            } catch (e) {
                if (!cancelled) {
                    setAlert("danger", friendlyError(e, t("clients.alerts.failLoadAppointments")));
                }
            } finally {
                if (!cancelled) setLoadingAppointments(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [draft?.id, getAccessTokenSilently, setAlert, t]);

    // Load the caller's CLIENT_HISTORY grant + team members (to label note
    // authors). Fail-closed: no grant → history/notes tabs hidden.
    useEffect(() => {
        if (!businessIdStr) { setHistoryGrant(null); setTeamMembers([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const [grant, clientG, tms, maxDisc] = await Promise.all([
                    getMyPermissionGrant(businessIdStr, "CLIENT_HISTORY", token).catch(() => null),
                    getMyPermissionGrant(businessIdStr, "CLIENTS", token).catch(() => null),
                    getTeamMembersByBusinessId(businessIdStr, token).catch(() => [] as TeamMember[]),
                    getMyMaxDiscountRate(businessIdStr, token).catch(() => 0),
                ]);
                if (!cancelled) { setHistoryGrant(grant); setClientGrant(clientG); setTeamMembers(tms); setMaxDiscountRate(maxDisc); }
            } catch {
                if (!cancelled) { setHistoryGrant(null); setClientGrant(null); setTeamMembers([]); setMaxDiscountRate(0); }
            }
        })();
        return () => { cancelled = true; };
    }, [businessIdStr, getAccessTokenSilently]);

    // Load client annotations when a client is open AND the caller can view
    // history. Scope (own/all) is applied server-side.
    useEffect(() => {
        const id = draft?.id;
        if (!id || !canViewHistory) { setClientNotes([]); return; }
        let cancelled = false;
        (async () => {
            try {
                setLoadingNotes(true);
                const token = await getAccessTokenSilently();
                const data = await getClientNotes(String(id), token);
                if (!cancelled) setClientNotes(data);
            } catch (e) {
                if (!cancelled) setAlert("danger", friendlyError(e, t("clients.alerts.failLoadNotes")));
            } finally {
                if (!cancelled) setLoadingNotes(false);
            }
        })();
        return () => { cancelled = true; };
    }, [draft?.id, canViewHistory, getAccessTokenSilently, setAlert, t]);

    /** Display name for a note's author (team member), falling back to the JWT
     *  subject the backend stored. */
    const noteAuthorName = useCallback((n: ClientNote): string => {
        if (n.teamMemberId != null) {
            const tm = teamMembers.find((m) => String(m.id) === String(n.teamMemberId));
            if (tm) return `${tm.firstName ?? ""} ${tm.lastName ?? ""}`.trim() || "—";
        }
        return n.createdBy || "—";
    }, [teamMembers]);

    const handleAddNote = async () => {
        const id = draft?.id;
        const text = newNoteText.trim();
        if (!id || !text) return;
        if (!canEditHistory) {
            setAlert("danger", t("clients.alerts.noPermissionAddNote"));
            return;
        }
        try {
            setSavingNote(true);
            const token = await getAccessTokenSilently();
            const saved = await saveClientNote({ clientId: String(id), note: text }, token);
            setClientNotes((prev) => [saved, ...prev]);
            setNewNoteText("");
        } catch (e) {
            setAlert("danger", friendlyError(e, t("clients.alerts.failSaveNote")));
        } finally {
            setSavingNote(false);
        }
    };

    const handleDeleteNote = async (noteId: string) => {
        if (!canEditHistory) {
            setAlert("danger", t("clients.alerts.noPermissionDeleteNote"));
            return;
        }
        try {
            const token = await getAccessTokenSilently();
            await deleteClientNote(noteId, token);
            setClientNotes((prev) => prev.filter((n) => String(n.id) !== String(noteId)));
        } catch (e) {
            setAlert("danger", friendlyError(e, t("clients.alerts.failDeleteNote")));
        }
    };

    // If the active tab becomes unavailable (lost history permission / no
    // client), fall back to Info so the drawer never shows a blank tab.
    useEffect(() => {
        if ((activeTab === "appointments" || activeTab === "notes") && !canViewHistory) {
            setActiveTab("info");
        }
    }, [activeTab, canViewHistory]);

    const handleLoadInactive = () => {
        if (!inactiveMatch || !draft) return;
        // Keep what the user already typed; fill in only the blanks.
        setDraft({
            ...draft,
            firstName: draft.firstName?.trim() || inactiveMatch.firstName || "",
            lastName: draft.lastName?.trim() || inactiveMatch.lastName || "",
            email: draft.email?.trim() || inactiveMatch.email || "",
            phone: draft.phone?.trim() || inactiveMatch.phone || "",
            notes: draft.notes?.trim() || inactiveMatch.notes || "",
        });
        setPendingReactivationId(inactiveMatch.id);
        setInactiveMatch(null);
    };

    const dismissInactiveMatch = () => {
        setInactiveMatch(null);
    };

    const handleField = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!draft.firstName.trim()) {
            setAlert("danger", t("clients.alerts.firstNameRequired"));
            return;
        }
        if (!businessIdStr) {
            setAlert("danger", t("clients.alerts.noBusinessSelected"));
            return;
        }
        const phoneCountry = resolvePhoneCountry(undefined, selectedBusiness?.currency);
        if (!isPhoneComplete(draft.phone ?? '', phoneCountry)) {
            setAlert("danger", t("common.invalidPhone"));
            return;
        }
        // Birthday validation: day and month must be provided together.
        const hasDay = draft.birthDay != null && draft.birthDay > 0;
        const hasMonth = draft.birthMonth != null && draft.birthMonth > 0;
        if (hasDay !== hasMonth) {
            setAlert("danger", t("clients.birthdayIncomplete"));
            return;
        }
        // Year is only allowed alongside day+month; validate range.
        const hasYear = draft.birthYear != null && draft.birthYear > 0;
        if (hasYear) {
            const currentYear = new Date().getFullYear();
            if (!hasDay || draft.birthYear! < 1900 || draft.birthYear! > currentYear) {
                setAlert("danger", t("clients.birthdayYearInvalid", { max: currentYear }));
                return;
            }
        }
        // Convênios (QA decision 2026-07-04): a partially-filled row BLOCKS the save
        // instead of being silently dropped — the user thought it was saved before.
        if (draft.convenios) {
            const filled = draft.convenios.filter((cv) =>
                !!cv.healthPlanId || !!(cv.cardNumber && cv.cardNumber.trim()) ||
                !!cv.validThrough || !!cv.holderType || !!(cv.holderName && cv.holderName.trim()));
            for (let i = 0; i < filled.length; i++) {
                const cv = filled[i];
                if (!cv.healthPlanId || !(cv.cardNumber && cv.cardNumber.trim())) {
                    setActiveTab("convenios");
                    setAlert("danger", t("clients.convenios.incompleteRow"));
                    return;
                }
                if (cv.holderType === "DEPENDENTE" && !(cv.holderName && cv.holderName.trim())) {
                    setActiveTab("convenios");
                    setAlert("danger", t("clients.convenios.holderNameRequired"));
                    return;
                }
            }
            const planIds = filled.map((cv) => String(cv.healthPlanId));
            if (new Set(planIds).size !== planIds.length) {
                setActiveTab("convenios");
                setAlert("danger", t("clients.convenios.duplicatePlan"));
                return;
            }
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();

            let payload: ClientInput = { ...draft, businessId: businessIdStr };
            // Drop fully-EMPTY convênio rows before sending; keep the array (even
            // empty) so the backend replaces the client's convênios. Partial rows
            // never get here — they block the save above.
            if (draft.convenios) {
                payload.convenios = draft.convenios.filter(
                    (cv) => !!cv.healthPlanId && !!(cv.cardNumber && cv.cardNumber.trim())
                );
            }
            // Reactivation flow primed by the banner: reuse the existing id and force active=true.
            if (!draft.id && pendingReactivationId) {
                payload = { ...payload, id: pendingReactivationId, active: true };
            }

            const saved = await saveClient(payload, token);
            setAlert(
                "success",
                t("clients.alerts.saved", {
                    name: `${saved.firstName}${saved.lastName ? " " + saved.lastName : ""}`,
                })
            );
            // Close the drawer on success (don't re-open the editor).
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert("danger", friendlyError(e, t("clients.alerts.failSave")));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft?.id) return;
        const name = `${draft.firstName}${draft.lastName ? " " + draft.lastName : ""}`;
        if (!(await confirmDialog({ title: t("common.confirmTitle"), message: t("clients.confirmDelete", { name }), danger: true, confirmLabel: t("common.delete") }))) return;
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await deleteClient(draft.id, token);
            setAlert("success", t("clients.alerts.deleted"));
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert("danger", friendlyError(e, t("clients.alerts.failDelete")));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingComponent />;

    const totalPages = Math.max(1, Math.ceil(filteredClients.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * perPage;
    const visibleClients = filteredClients.slice(pageStart, pageStart + perPage);

    return (
        <div className="clients-page" data-cy="clients-page">

            <div className="list-card" data-cy="clients-list">
                <div className="list-header">
                    <h5 className="mb-0">{t("clients.title")}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>⌕</span>
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder={t("clients.searchPlaceholder")}
                            value={searchTerm}
                            data-cy="clients-search-input"
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(1);
                            }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => {
                                    setSearchTerm("");
                                    setPage(1);
                                }}
                                aria-label={t("common.clear")}
                                title={t("common.clear")}
                                data-cy="clients-search-clear"
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <div className="clients-tabs">
                        <button
                            type="button"
                            className={`clients-tab ${!favoritesOnly ? "clients-tab--active" : ""}`}
                            onClick={() => { setFavoritesOnly(false); setPage(1); }}
                            data-cy="clients-tab-all"
                        >
                            {t("clients.tabAll")}
                        </button>
                        <button
                            type="button"
                            className={`clients-tab ${favoritesOnly ? "clients-tab--active" : ""}`}
                            onClick={() => { setFavoritesOnly(true); setPage(1); }}
                            data-cy="clients-tab-favorites"
                        >
                            {t("clients.tabFavorites")}
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={startCreate}
                        data-cy="clients-add-btn"
                    >
                        {t("clients.addNew")}
                    </button>
                </div>

                {filteredClients.length === 0 && (
                    <div className="empty-hint">
                        {clients.length === 0
                            ? t("clients.emptyList")
                            : t("clients.emptySearch")}
                    </div>
                )}

                {visibleClients.map((c) => (
                    <div
                        key={c.id}
                        className={`list-item ${selectedId === c.id ? "selected" : ""}`}
                        onClick={() => startEdit(c)}
                        data-cy={`clients-row-${c.id}`}
                    >
                        <div className="avatar">
                                {c.avatar
                                    ? <img
                                        src={mediaUrl(c.avatar)}
                                        alt={initialsOf(c)}
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                    />
                                    : initialsOf(c)}
                            </div>
                        <div className="info">
                            <div className="name">
                                {c.firstName}{c.lastName ? ` ${c.lastName}` : ""}
                            </div>
                            <div className="meta">
                                {c.email || c.phone || "—"}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={`client-fav ${c.favorite ? "client-fav--on" : ""}`}
                            onClick={(e) => toggleFavorite(c, e)}
                            title={c.favorite ? t("clients.favoriteOff") : t("clients.favoriteOn")}
                            aria-label={c.favorite ? t("clients.favoriteOff") : t("clients.favoriteOn")}
                            data-cy={`clients-favorite-btn-${c.id}`}
                        >
                            {c.favorite ? "★" : "☆"}
                        </button>
                    </div>
                ))}

                <Pagination
                    page={safePage}
                    perPage={perPage}
                    totalItems={filteredClients.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => {
                        setPerPage(n);
                        setPage(1);
                    }}
                    itemLabel="client"
                />
            </div>

            <Drawer
                open={!!draft}
                onClose={cancelEdit}
                title={
                    draft?.id ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                            {t("clients.drawerEdit")}
                            {(() => {
                                const cur = clients.find((x) => String(x.id) === String(draft.id));
                                return cur ? (
                                    <>
                                        <button
                                            type="button"
                                            className={`client-fav ${cur.favorite ? "client-fav--on" : ""}`}
                                            onClick={() => toggleFavorite(cur)}
                                            title={cur.favorite ? t("clients.favoriteOff") : t("clients.favoriteOn")}
                                        >
                                            {cur.favorite ? "★" : "☆"}
                                        </button>
                                        {cur.email && cur.registered ? (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary btn-sm"
                                                style={{ fontSize: "0.76rem", padding: "0.2rem 0.65rem" }}
                                                onClick={() => {
                                                    const name = [cur.firstName, cur.lastName].filter(Boolean).join(" ").trim() || cur.email || "";
                                                    setPendingChatClient({ email: cur.email!, name });
                                                    setActiveMenu(MENU.CHATS);
                                                }}
                                                title={t("clients.openChat")}
                                            >
                                                {t("clients.openChat")}
                                            </button>
                                        ) : cur.email ? (
                                            <small className="text-muted ms-1" style={{ fontSize: "0.72rem" }}>
                                                {t("clients.noGetServiceAccount")}
                                            </small>
                                        ) : null}
                                    </>
                                ) : null;
                            })()}
                        </span>
                    ) : t("clients.drawerNew")
                }
                size="xl"
                footer={
                    draft && (
                        <>
                            {canEditClient && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                    data-cy="clients-save-btn"
                                >
                                    {saving
                                        ? t("common.working")
                                        : pendingReactivationId
                                            ? t("clients.reactivateAndSave")
                                            : t("common.save")}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={cancelEdit}
                                disabled={saving}
                                data-cy="clients-cancel-btn"
                            >
                                {canEditClient ? t("common.cancel") : t("common.close")}
                            </button>
                            {draft.id && canEditClient && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    onClick={handleDelete}
                                    disabled={saving}
                                    data-cy="clients-delete-btn"
                                >
                                    {t("common.delete")}
                                </button>
                            )}
                        </>
                    )
                }
            >
                {draft && (
                    <div data-cy="clients-drawer">
                        {/* ---- Tabs (only meaningful for existing clients) ---- */}
                        {draft.id && (
                            <div className="gs-tab-strip">
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === "info" ? "gs-tab--active" : ""}`}
                                    onClick={() => setActiveTab("info")}
                                    data-cy="clients-tab-details"
                                >
                                    {t("clients.tabInfo")}
                                </button>
                                {/* Histórico & anotações tabs — gated by CLIENT_HISTORY. */}
                                {canViewHistory && (
                                    <button
                                        type="button"
                                        className={`gs-tab ${activeTab === "appointments" ? "gs-tab--active" : ""}`}
                                        onClick={() => setActiveTab("appointments")}
                                        data-cy="clients-tab-appointments"
                                    >
                                        {t("clients.tabHistory")}
                                        {clientAppointments.length > 0 && (
                                            <span className="gs-tab__count">
                                                {clientAppointments.length}
                                            </span>
                                        )}
                                    </button>
                                )}
                                {canViewHistory && (
                                    <button
                                        type="button"
                                        className={`gs-tab ${activeTab === "notes" ? "gs-tab--active" : ""}`}
                                        onClick={() => setActiveTab("notes")}
                                        data-cy="clients-tab-notes"
                                    >
                                        {t("clients.tabNotes")}
                                        {clientNotes.length > 0 && (
                                            <span className="gs-tab__count">
                                                {clientNotes.length}
                                            </span>
                                        )}
                                    </button>
                                )}
                                {healthMode && (
                                    <button
                                        type="button"
                                        className={`gs-tab ${activeTab === "prontuario" ? "gs-tab--active" : ""}`}
                                        onClick={() => setActiveTab("prontuario")}
                                        data-cy="clients-tab-prontuario"
                                    >
                                        {t("clinical.tab")}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === "discount" ? "gs-tab--active" : ""}`}
                                    onClick={() => setActiveTab("discount")}
                                    data-cy="clients-tab-discount"
                                >
                                    {t("clients.tabDiscount")}
                                </button>
                                <button
                                    type="button"
                                    className={`gs-tab ${activeTab === "convenios" ? "gs-tab--active" : ""}`}
                                    onClick={() => setActiveTab("convenios")}
                                    data-cy="clients-tab-convenios"
                                >
                                    {t("clients.tabConvenios")}
                                    {convenios.length > 0 && (
                                        <span className="gs-tab__count">{convenios.length}</span>
                                    )}
                                </button>
                                {draft.id && (
                                    <button
                                        type="button"
                                        className={`gs-tab ${activeTab === "payment" ? "gs-tab--active" : ""}`}
                                        onClick={() => setActiveTab("payment")}
                                        data-cy="clients-tab-payment"
                                    >
                                        {t("clients.tabPayment")}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* ---- INFO tab ---- */}
                        {activeTab === "info" && (
                            <>
                                {inactiveMatch && !draft.id && (
                                    <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2 py-2">
                                        <div className="flex-grow-1 small">
                                            🔄 {t("clients.reactivateBanner")}&nbsp;
                                            <strong>
                                                {inactiveMatch.firstName}
                                                {inactiveMatch.lastName ? ` ${inactiveMatch.lastName}` : ""}
                                            </strong>
                                            {inactiveMatch.email && <> · {inactiveMatch.email}</>}
                                            {inactiveMatch.phone && <> · {inactiveMatch.phone}</>}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-warning"
                                            onClick={handleLoadInactive}
                                        >
                                            {t("clients.reactivateBannerLoad")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={dismissInactiveMatch}
                                        >
                                            {t("clients.reactivateBannerDismiss")}
                                        </button>
                                    </div>
                                )}

                                {pendingReactivationId && !draft.id && (
                                    <div className="alert alert-info py-2 small mb-3">
                                        {t("clients.reactivateHint")}
                                    </div>
                                )}

                                <div className="row">
                                    <div className="col-md-6 form-row">
                                        <label className="form-label">
                                            {t("clients.firstName")} <span className="gs-required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={draft.firstName}
                                            maxLength={100}
                                            onChange={(e) => handleField("firstName", e.target.value)}
                                            data-cy="clients-firstName-input"
                                        />
                                    </div>
                                    <div className="col-md-6 form-row">
                                        <label className="form-label">{t("clients.lastName")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={draft.lastName ?? ""}
                                            maxLength={100}
                                            onChange={(e) => handleField("lastName", e.target.value)}
                                            data-cy="clients-lastName-input"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-7 form-row">
                                        <label className="form-label">{t("clients.email")}</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            value={draft.email ?? ""}
                                            maxLength={255}
                                            onChange={(e) => handleField("email", e.target.value)}
                                            data-cy="clients-email-input"
                                        />
                                    </div>
                                    <div className="col-md-5 form-row">
                                        <label className="form-label">{t("clients.phone")}</label>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            className="form-control"
                                            value={draft.phone ?? ""}
                                            maxLength={16}
                                            onChange={(e) => {
                                                const country = resolvePhoneCountry(undefined, selectedBusiness?.currency);
                                                handleField("phone", formatPhone(e.target.value, country));
                                            }}
                                            data-cy="clients-phone-input"
                                        />
                                        {!isPhoneComplete(draft.phone ?? '', resolvePhoneCountry(undefined, selectedBusiness?.currency)) &&
                                         (draft.phone ?? '') !== '' && (
                                            <span className="form-text text-danger small">{t("common.invalidPhone")}</span>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <label className="form-label">{t("clients.notes")}</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={draft.notes ?? ""}
                                        maxLength={5000}
                                        onChange={(e) => handleField("notes", e.target.value)}
                                        data-cy="clients-notes-input"
                                    />
                                    {(draft.notes ?? "").length >= 4500 && (
                                        <div className="form-text text-end small">
                                            {(draft.notes ?? "").length}/5000
                                        </div>
                                    )}
                                </div>

                                {/* ---- Birthday ---- */}
                                <div className="form-row">
                                    <label className="form-label">{t("clients.birthday")}</label>
                                    <div className="row g-2">
                                        <div className="col-3">
                                            <select
                                                className="form-select"
                                                value={draft.birthDay ?? ""}
                                                onChange={(e) =>
                                                    handleField(
                                                        "birthDay",
                                                        e.target.value === "" ? null : Number(e.target.value)
                                                    )
                                                }
                                                aria-label={t("clients.birthdayDay")}
                                                data-cy="clients-birthDay-select"
                                            >
                                                <option value="">{t("clients.birthdayDay")}</option>
                                                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                                    <option key={d} value={d}>{d}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-5">
                                            <select
                                                className="form-select"
                                                value={draft.birthMonth ?? ""}
                                                onChange={(e) =>
                                                    handleField(
                                                        "birthMonth",
                                                        e.target.value === "" ? null : Number(e.target.value)
                                                    )
                                                }
                                                aria-label={t("clients.birthdayMonth")}
                                                data-cy="clients-birthMonth-select"
                                            >
                                                <option value="">{t("clients.birthdayMonth")}</option>
                                                {(
                                                    [
                                                        "january", "february", "march", "april",
                                                        "may", "june", "july", "august",
                                                        "september", "october", "november", "december",
                                                    ] as const
                                                ).map((key, idx) => (
                                                    <option key={idx + 1} value={idx + 1}>
                                                        {t(`common.months.${key}`)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-4">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="form-control"
                                                maxLength={4}
                                                placeholder={t("clients.birthdayYearOptional")}
                                                value={draft.birthYear ?? ""}
                                                onChange={(e) => {
                                                    // Digits only — type="number" accepted e/+/- (QA-STANDARDS §2).
                                                    const digits = e.target.value.replace(/\D/g, "");
                                                    handleField("birthYear", digits === "" ? null : Number(digits));
                                                }}
                                                aria-label={t("clients.birthdayYear")}
                                                data-cy="clients-birthYear-input"
                                            />
                                        </div>
                                    </div>
                                    {/* Inline validation hint — shown when exactly one of day/month is set */}
                                    {((draft.birthDay != null && draft.birthDay > 0) !==
                                        (draft.birthMonth != null && draft.birthMonth > 0)) && (
                                        <span className="form-text text-danger small">
                                            {t("clients.birthdayIncomplete")}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}

                        {/* ---- APPOINTMENTS tab ---- */}
                        {activeTab === "appointments" && (
                            <div className="gs-client-history">
                                {loadingAppointments && (
                                    <div className="gs-hint">{t("clients.loadingAppointments")}</div>
                                )}
                                {!loadingAppointments && clientAppointments.length === 0 && (
                                    <div className="gs-hint">
                                        {t("clients.noAppointments")}
                                    </div>
                                )}
                                {clientAppointments.map((a) => {
                                    const start = new Date(a.startTime);
                                    const end = new Date(a.endTime);
                                    const isPast = end.getTime() < Date.now();
                                    return (
                                        <div
                                            key={a.id}
                                            className={`gs-client-history__item gs-client-history__item--link ${
                                                isPast ? "gs-client-history__item--past" : ""
                                            }`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openAppointmentDetail(a.id)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    openAppointmentDetail(a.id);
                                                }
                                            }}
                                            title={t("clients.openAppointment")}
                                        >
                                            <div className="gs-client-history__when">
                                                <span className="gs-client-history__id">#{a.id}</span>
                                                <strong>
                                                    {start.toLocaleDateString(undefined, {
                                                        weekday: "short",
                                                        month: "short",
                                                        day: "numeric",
                                                        year:
                                                            start.getFullYear() ===
                                                            new Date().getFullYear()
                                                                ? undefined
                                                                : "numeric",
                                                    })}
                                                </strong>
                                                <span className="gs-client-history__time">
                                                    {formatClock(start, timeFmt)}
                                                    {" – "}
                                                    {formatClock(end, timeFmt)}
                                                </span>
                                                <span
                                                    className={`gs-client-history__status gs-client-history__status--${a.status.toLowerCase()}`}
                                                >
                                                    {a.status}
                                                </span>
                                            </div>
                                            <div className="gs-client-history__customer">
                                                {a.customerName}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ---- NOTES tab (client-level notes, CLIENT_HISTORY) ---- */}
                        {activeTab === "prontuario" && selectedId && (
                            <ClinicalRecordsTab
                                businessId={Number(selectedBusiness?.id) || 0}
                                clientId={Number(selectedId)}
                            />
                        )}
                        {activeTab === "notes" && (
                            <div className="gs-client-history">
                                {healthMode && (
                                    <div className="alert alert-warning py-1 px-2 mb-2" style={{ fontSize: '0.8rem' }} data-cy="client-notes-health-hint">
                                        {t('clients.healthNotesHint')}
                                    </div>
                                )}
                                {/* Add box — only when the caller can edit history. */}
                                {canEditHistory && (
                                    <div className="gs-client-note-add">
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            placeholder={t("clients.notePlaceholder")}
                                            value={newNoteText}
                                            maxLength={5000}
                                            onChange={(e) => setNewNoteText(e.target.value)}
                                            data-cy="clients-note-textarea"
                                        />
                                        <div className="d-flex justify-content-end mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={handleAddNote}
                                                disabled={savingNote || !newNoteText.trim()}
                                                data-cy="clients-add-note-btn"
                                            >
                                                {savingNote ? t("clients.savingNote") : t("clients.addNote")}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {loadingNotes && <div className="gs-hint">{t("clients.loadingNotes")}</div>}
                                {!loadingNotes && clientNotes.length === 0 && (
                                    <div className="gs-hint">
                                        {t("clients.noNotes")}
                                    </div>
                                )}
                                {clientNotes.map((n) => {
                                    const when = n.createdOn ? new Date(n.createdOn) : null;
                                    return (
                                        <div key={n.id} className="gs-client-history__item">
                                            <div className="gs-client-history__when">
                                                <strong>{noteAuthorName(n)}</strong>
                                                {when && (
                                                    <span className="gs-client-history__time">
                                                        {when.toLocaleDateString(undefined, {
                                                            month: "short",
                                                            day: "numeric",
                                                            year: "numeric",
                                                        })}{" "}
                                                        {formatClock(when, timeFmt)}
                                                    </span>
                                                )}
                                                {canEditHistory && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm text-danger ms-auto p-0"
                                                        onClick={() => handleDeleteNote(String(n.id))}
                                                        title={t("clients.removeNote")}
                                                    >
                                                        {t("clients.removeNote")}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="gs-client-history__note">{n.note}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ---- DISCOUNT tab ---- */}
                        {activeTab === "discount" && (() => {
                            // The client discount is bounded strictly by the editor's
                            // DISCOUNT tier (maxDiscountRate). Tier 0 ("Nenhum") = the
                            // field is locked: the caller can't set OR change a discount,
                            // even on a client that already has one. Backend re-validates
                            // (a changed value above the tier is rejected).
                            const tier = maxDiscountRate ?? 0;
                            const cap = tier;
                            const locked = tier <= 0;
                            return (
                                <div className="form-row">
                                    <label className="form-label">{t("clients.discountLabel")}</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        maxLength={5}
                                        className="form-control"
                                        style={{ maxWidth: 160 }}
                                        disabled={locked}
                                        data-cy="clients-discount-input"
                                        value={discountText}
                                        onChange={(e) => {
                                            // Digits + one decimal separator only; then clamp
                                            // to the tier cap so the UI never offers more than
                                            // the person is allowed to give.
                                            let clean = e.target.value
                                                .replace(/[^0-9.,]/g, "")
                                                .replace(",", ".")
                                                .replace(/^(\d*\.\d*).*$/, "$1");
                                            if (clean === "") {
                                                setDiscountText("");
                                                handleField("discountPercent", null);
                                                return;
                                            }
                                            const num = parseFloat(clean);
                                            if (!Number.isNaN(num) && num > cap) clean = String(cap);
                                            setDiscountText(clean);
                                            handleField("discountPercent", Number.isNaN(parseFloat(clean)) ? null : parseFloat(clean));
                                        }}
                                        placeholder="0"
                                    />
                                    <div className="form-text mt-2">
                                        {t("clients.discountHint")}
                                    </div>
                                    {locked ? (
                                        <div className="form-text text-danger mt-1">
                                            {t("clients.discountLockedHint")}
                                        </div>
                                    ) : (
                                        <div className="form-text mt-1">
                                            {t("clients.discountLimitHint", { cap })}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ---- CONVÊNIOS (health-plan memberships) tab ---- */}
                        {activeTab === "convenios" && (
                            <div className="form-row">
                                <label className="form-label">{t("clients.convenios.title")}</label>
                                <div className="form-text mb-2">{t("clients.convenios.hint")}</div>
                                {healthPlans.length === 0 && (
                                    <div className="alert alert-warning py-2 small mb-2">
                                        {t("clients.convenios.noPlans")}
                                    </div>
                                )}
                                {convenios.map((cv, idx) => (
                                    <div key={idx} className="border rounded p-2 mb-2">
                                        <div className="gs-form-grid">
                                            <div>
                                                <label className="form-label small mb-1">{t("clients.convenios.plan")}</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={cv.healthPlanId ?? ""}
                                                    onChange={(e) => patchConvenio(idx, { healthPlanId: e.target.value })}
                                                    disabled={healthPlans.length === 0}
                                                >
                                                    <option value="">{t("clients.convenios.selectPlan")}</option>
                                                    {healthPlans.map((p) => (
                                                        <option key={p.id} value={String(p.id)}>{p.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="form-label small mb-1">{t("clients.convenios.card")}</label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    value={cv.cardNumber ?? ""}
                                                    maxLength={60}
                                                    onChange={(e) => patchConvenio(idx, { cardNumber: e.target.value })}
                                                    placeholder={t("clients.convenios.cardPlaceholder")}
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label small mb-1">{t("clients.convenios.validThrough")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={cv.validThrough ?? ""}
                                                    onChange={(e) => patchConvenio(idx, { validThrough: e.target.value || null })}
                                                />
                                                {/* Past validity is allowed (history) but flagged. */}
                                                {cv.validThrough && cv.validThrough < new Date().toISOString().slice(0, 10) && (
                                                    <span className="form-text text-warning small d-block">
                                                        {t("clients.convenios.expired")}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <label className="form-label small mb-1">{t("clients.convenios.holder")}</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={cv.holderType ?? ""}
                                                    onChange={(e) => patchConvenio(idx, { holderType: e.target.value || null })}
                                                >
                                                    <option value="">—</option>
                                                    <option value="TITULAR">{t("clients.convenios.titular")}</option>
                                                    <option value="DEPENDENTE">{t("clients.convenios.dependente")}</option>
                                                </select>
                                            </div>
                                            {cv.holderType === "DEPENDENTE" && (
                                                <div>
                                                    <label className="form-label small mb-1">{t("clients.convenios.holderName")}</label>
                                                    <input
                                                        className="form-control form-control-sm"
                                                        value={cv.holderName ?? ""}
                                                        maxLength={200}
                                                        onChange={(e) => patchConvenio(idx, { holderName: e.target.value || null })}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger mt-2"
                                            onClick={() => removeConvenio(idx)}
                                        >
                                            {t("clients.convenios.remove")}
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary mt-1"
                                    onClick={addConvenio}
                                    disabled={healthPlans.length === 0}
                                >
                                    + {t("clients.convenios.add")}
                                </button>
                            </div>
                        )}

                        {/* ---- PAYMENT (card on file) tab ---- */}
                        {activeTab === "payment" && draft.id && (() => {
                            const current = clients.find((c) => String(c.id) === String(draft.id));
                            if (!current) {
                                return <p className="text-muted">{t("clients.saveBusiness")}</p>;
                            }
                            return (
                                <div className="form-row">
                                    <label className="form-label">{t("clients.cardOnFile")}</label>
                                    <ClientCardSection
                                        businessId={businessIdStr}
                                        client={current}
                                        onChanged={reload}
                                    />
                                    <div className="form-text mt-2">
                                        {t("clients.cardTokenHint")}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default ClientsComponent;
